import IconButton from '@ds/components/actions/IconButton';
import { Flex } from '@ds/components/layout/Flex';
import Typography, { type TypographyColor } from '@ds/components/typography/Typography';
import { CloseIcon, ErrorIcon, type IconProps, InfoIcon, StatusSuccessIcon, WarningIcon } from '@ds/icons';
import clsx from 'clsx';
import React, { forwardRef, type ReactNode } from 'react';

import styles from './Alert.module.css';

/** Status of the alert. Drives surface, border, icon, and (with emphasis) text colour. */
export type AlertVariant = 'danger' | 'success' | 'info' | 'warning';

/** Light/Dark axis. `subtle` = Figma Light (tinted); `solid` = Figma Dark (saturated). */
export type AlertEmphasis = 'subtle' | 'solid';

type IconComponent = React.ComponentType<IconProps>;

/**
 * Status-icon size, in px. The block form gets Figma's 30px glyph; the compact
 * single-line form drops to 20px to sit on the text row. Sizing lives here rather
 * than in CSS because `compact` is a prop. The close glyph is not sized here at
 * all: it is an `IconButton` at its default `sm`, which owns its 18px glyph and
 * 40px hit target.
 */
const ICON_SIZE = 30;
const ICON_SIZE_COMPACT = 20;

/** Each variant auto-selects its status icon (all icons use `currentColor`). */
const VARIANT_ICON: Record<AlertVariant, IconComponent> = {
    danger: ErrorIcon,
    success: StatusSuccessIcon,
    info: InfoIcon,
    warning: WarningIcon,
};

/** Danger/warning announce assertively; info/success are polite status updates. */
const VARIANT_ROLE: Record<AlertVariant, 'alert' | 'status'> = {
    danger: 'alert',
    warning: 'alert',
    info: 'status',
    success: 'status',
};

export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
    /** Status of the alert. Drives the colour scheme and icon. Defaults to `info`. */
    variant?: AlertVariant;
    /** Light (`subtle`) or Dark (`solid`) surface. Defaults to `subtle`. */
    emphasis?: AlertEmphasis;
    /** Bold heading line (rendered as `heading-2`). */
    title?: ReactNode;
    /** Supporting copy beneath the heading (rendered as `body-1`). */
    subtitle?: ReactNode;
    /** Rich body content (e.g. an error `<ul>`); rendered after `subtitle`. */
    children?: ReactNode;
    /**
     * Show a trailing close button (block layout only). Defaults to whether
     * `onClose` is supplied, so existing callers are unaffected; pass it
     * explicitly to force the button on/off independently of the handler.
     */
    dismissible?: boolean;
    /** Called when the close button is clicked. The caller removes the alert. */
    onClose?: () => void;
    /** Show the leading status icon. Defaults to `true`. */
    showIcon?: boolean;
    /**
     * Render the Figma flat *compact* counterpart: a single line and no close
     * button. `title` (`title-1`) and `subtitle` (`body-1`) run inline on the same
     * row, so toggling `compact` on a block-configured alert still shows its
     * message. `children`, when given, replaces the whole line as `title-1`.
     * `onClose` is ignored. Defaults to `false` (full block).
     */
    compact?: boolean;
    /**
     * Shrink to hug the message instead of filling the container
     * (`width: fit-content`). By default the alert spans its container.
     */
    fitContent?: boolean;
}

/**
 * The prominent boxed alert (Figma *In-page alert*, non-compact): a status icon,
 * a `heading-2` title, body copy, and an optional close button. It sits in
 * the page, fills its container and casts no shadow (Figma Placement = In page);
 * a message that floats over the page is a separate placement, not a prop here. `variant` sets the status colour and icon;
 * `emphasis` switches between Figma's Light (`subtle`) and Dark (`solid`) skins.
 *
 * The compact form (Figma *In-page alert · Compact*, formerly "Alert Simple")
 * is the one single-line notice — it replaced the retired `InlineAlert`
 * (ADR 0008; ENVDB-9474). It has no minimum width, so it fits narrow cards.
 *
 * @example
 * <Alert variant="danger" title="There was an error while saving">
 *     <ul>{messages.map((m) => <li key={m}>{m}</li>)}</ul>
 * </Alert>
 *
 * @example
 * <Alert variant="success" emphasis="solid" onClose={hide} title="Saved!" />
 *
 * @example
 * // Compact single-line counterpart — title-1 title and body-1 subtitle inline, no close:
 * <Alert variant="info" compact title="Text in here." subtitle="Text in here" />
 */
const Alert = forwardRef<HTMLDivElement, AlertProps>(
    (
        {
            variant = 'info',
            emphasis = 'subtle',
            title,
            subtitle,
            children,
            dismissible,
            onClose,
            showIcon = true,
            compact = false,
            fitContent = false,
            className,
            ...rest
        },
        ref
    ) => {
        const Icon = VARIANT_ICON[variant];
        // On a solid surface text is white; subtle keeps the default primary.
        const textColor: TypographyColor = emphasis === 'solid' ? 'inverse' : 'primary';
        // The close button defaults to whether a handler was supplied — overridable.
        const showClose = (dismissible ?? onClose != null) && onClose != null;

        // These are the title and the subtitle of the block form. The compact form renders its own inline run below.
        const blockTitle = title && (
            <Typography
                variant="heading-2"
                color={textColor}
            >
                {title}
            </Typography>
        );
        const blockSubtitle = subtitle && (
            <Typography
                variant="body-1"
                color={textColor}
                // `subtitle` is caller-supplied ReactNode and several call sites
                // (e.g. AccreditationStatus.tsx) pass a fragment of their own
                // <Typography variant="body-1"> (<p>) blocks. The default body-1
                // element is also <p>, so without this override a caller's <p>
                // ends up nested inside this one — invalid HTML the browser
                // silently "fixes" by closing the outer <p> early, which detaches
                // the DOM from the JSX tree. Render as a <div> instead; body-1's
                // styling is entirely data-attribute driven (Typography.module.css),
                // so this is a purely semantic change with no visual effect.
                component="div"
            >
                {subtitle}
            </Typography>
        );

        return (
            <div
                ref={ref}
                role={VARIANT_ROLE[variant]}
                data-variant={variant}
                data-emphasis={emphasis}
                data-compact={compact || undefined}
                data-fitcontent={fitContent || undefined}
                className={clsx(styles.alert, className)}
                {...rest}
            >
                {showIcon && (
                    <span
                        className={styles.icon}
                        aria-hidden="true"
                    >
                        <Icon size={compact ? ICON_SIZE_COMPACT : ICON_SIZE} />
                    </span>
                )}
                {compact ? (
                    // One run of text, as Figma draws it: the title and subtitle sit inline
                    // on the same row and wrap together, rather than stacking as in the block.
                    <span className={styles.text}>
                        {(children ?? title) != null && (
                            <Typography
                                variant="title-1"
                                component="span"
                                color={textColor}
                            >
                                {children ?? title}
                            </Typography>
                        )}
                        {children == null && subtitle != null && (
                            <>
                                {title != null && ' '}
                                <Typography
                                    variant="body-1"
                                    component="span"
                                    color={textColor}
                                >
                                    {subtitle}
                                </Typography>
                            </>
                        )}
                    </span>
                ) : (
                    <>
                        <div className={styles.content}>
                            {title && subtitle ? (
                                // Figma 14647:3354 sets 12px (stack/space-3) from a heading-2 to the text
                                // under it. This group holds that gap. `.content` keeps 8px between the
                                // group and `children`. A lone title or subtitle needs no group.
                                <Flex
                                    direction="column"
                                    gap={3}
                                >
                                    {blockTitle}
                                    {blockSubtitle}
                                </Flex>
                            ) : (
                                <>
                                    {blockTitle}
                                    {blockSubtitle}
                                </>
                            )}
                            {children}
                        </div>
                        {showClose && (
                            <IconButton
                                icon={<CloseIcon />}
                                label="Close"
                                variant="text"
                                shape="circle"
                                className={styles.close}
                                onClick={onClose}
                            />
                        )}
                    </>
                )}
            </div>
        );
    }
);

Alert.displayName = 'Alert';

export { Alert };
export default Alert;
